import axios from "axios";

import { axiosClient } from "@/lib/api";

import { deviceHeaders } from "./device-setup-api";

/**
 * PIN login (NAU-227). A user selects their name on a linked shared device and
 * enters their PIN; the server returns a Firebase custom token to redeem for a
 * session. There is no user session yet, so no Authorization header is sent —
 * the device authenticates itself with the secret it was issued at link time,
 * which is what limits these endpoints to devices a manager actually set up.
 */

export interface SignInWithPinResponse {
  customToken: string;
  userId: string;
}

/** Error codes the PIN-login endpoint returns in the response body. */
export const PinLoginErrorCode = {
  InvalidPin: "INVALID_PIN",
  AccountLocked: "ACCOUNT_LOCKED",
  PinNotSet: "PIN_NOT_SET",
  PinSetupRequired: "PIN_SETUP_REQUIRED",
  DeviceNotRecognised: "DEVICE_NOT_RECOGNISED",
} as const;

export interface PinLoginErrorBody {
  code?: string;
  message?: string;
  /** Present on INVALID_PIN — remaining attempts before lockout. */
  attemptsRemaining?: number;
  /** Present on PIN_SETUP_REQUIRED — a manager reset an active PIN. */
  wasResetByManager?: boolean;
}

export async function signInWithPin(
  deviceSecret: string,
  userId: string,
  pin: string,
): Promise<SignInWithPinResponse> {
  const res = await axiosClient<SignInWithPinResponse>({
    url: "/v1/auth/pin-login",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...deviceHeaders(deviceSecret),
    },
    data: { userId, pin },
  });
  return res.data;
}

/**
 * Complete first-time PIN setup: the temporary PIN re-authenticates the user
 * and the new PIN replaces it. Public and pre-auth like signInWithPin; returns
 * the same custom-token response, so success logs the user straight in.
 */
export async function completePinSetup(
  deviceSecret: string,
  userId: string,
  temporaryPin: string,
  newPin: string,
): Promise<SignInWithPinResponse> {
  const res = await axiosClient<SignInWithPinResponse>({
    url: "/v1/auth/pin-setup",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...deviceHeaders(deviceSecret),
    },
    data: { userId, temporaryPin, newPin },
  });
  return res.data;
}

/** How the PIN-login screen should react to a failed sign-in attempt. */
export type PinLoginErrorResult =
  | { kind: "setup"; wasResetByManager: boolean } // correct temporary PIN → first-time setup
  | { kind: "no-pin" } // no PIN at all → a manager must set a temporary one
  | { kind: "locked" }
  | { kind: "relink" } // the server no longer recognises this device
  | { kind: "invalid"; attemptsRemaining?: number }
  | { kind: "generic" };

/**
 * Map a PIN-login failure to the action the UI should take, keyed off the error
 * code (with HTTP status as a fallback). Pure so it can be unit-tested.
 */
export function interpretPinLoginError(err: unknown): PinLoginErrorResult {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const body = err.response?.data as PinLoginErrorBody | undefined;

    if (body?.code === PinLoginErrorCode.PinSetupRequired) {
      return { kind: "setup", wasResetByManager: !!body.wasResetByManager };
    }
    if (body?.code === PinLoginErrorCode.PinNotSet || status === 409) {
      return { kind: "no-pin" };
    }
    if (body?.code === PinLoginErrorCode.AccountLocked || status === 423) {
      return { kind: "locked" };
    }
    // Must be matched before the bare-401 fallback below, which would otherwise
    // tell the user their PIN was wrong when the device is the problem.
    if (body?.code === PinLoginErrorCode.DeviceNotRecognised) {
      return { kind: "relink" };
    }
    if (body?.code === PinLoginErrorCode.InvalidPin || status === 401) {
      return { kind: "invalid", attemptsRemaining: body?.attemptsRemaining };
    }
  }
  return { kind: "generic" };
}

/** True when the failure is a connectivity problem rather than a rejection. */
export function isNetworkError(err: unknown): boolean {
  return axios.isAxiosError(err) && !err.response;
}

/** Keep only digits, capped at the PIN length. */
export function sanitizePin(value: string, maxLength = 4): string {
  return value.replace(/\D/g, "").slice(0, maxLength);
}

/** The "Incorrect PIN. X attempts remaining." message (handles singular). */
export function invalidPinMessage(attemptsRemaining?: number): string {
  if (attemptsRemaining == null) {
    return "Incorrect PIN. Please try again.";
  }
  return `Incorrect PIN. ${attemptsRemaining} attempt${attemptsRemaining === 1 ? "" : "s"} remaining.`;
}
